import { useQuery } from "@tanstack/react-query";
import { apiGet, apiSend } from "../../../api/client";
import type {
  ConnectionCard,
  ConnectionMethod,
  CreateEntityRequest,
  DiscoveryResult,
  Entity,
  EntitySummary,
  Family,
  PatchEntityRequest,
  ProbeResult,
  ReadinessItem,
  ReadinessReport,
  TestDocumentResult,
} from "../../../api/types";

/** The company already holding this CR, if any (M2 ruling 22: `GET /api/entities?cr=`). */
export function useCrLookup(cr: string) {
  const trimmed = cr.trim();
  return useQuery({
    queryKey: ["entities", "cr", trimmed],
    queryFn: async () =>
      (await apiGet<{ items: EntitySummary[] }>(`/api/entities?cr=${encodeURIComponent(trimmed)}`)).items,
    enabled: trimmed.length > 0,
  });
}

export function useConnection(entityId: string | undefined, refetchIntervalMs?: number) {
  return useQuery({
    queryKey: ["entities", entityId, "connection"],
    queryFn: () => apiGet<ConnectionCard>(`/api/entities/${entityId}/connection`),
    enabled: Boolean(entityId),
    ...(refetchIntervalMs ? { refetchInterval: refetchIntervalMs } : {}),
  });
}

export function createEntity(body: CreateEntityRequest): Promise<Entity> {
  return apiSend<Entity>("POST", "/api/entities", body);
}

export function patchEntity(entityId: string, body: PatchEntityRequest): Promise<Entity> {
  return apiSend<Entity>("PATCH", `/api/entities/${entityId}`, body);
}

export function saveConnection(
  entityId: string,
  method: ConnectionMethod,
  config: Record<string, unknown>,
): Promise<ConnectionCard> {
  return apiSend<ConnectionCard>("PUT", `/api/entities/${entityId}/connection`, { method, config });
}

export function runProbe(entityId: string): Promise<ProbeResult> {
  return apiSend<ProbeResult>("POST", `/api/entities/${entityId}/connection/test`);
}

export function discoverFields(entityId: string, family?: string): Promise<DiscoveryResult> {
  const query = family ? `?family=${encodeURIComponent(family)}` : "";
  return apiSend<DiscoveryResult>(
    "POST",
    `/api/entities/${entityId}/connection/discover-fields${query}`,
  );
}

/** Discovery is per family: a Purchase Invoice family reads different ERP columns from a
    Sales Invoice one, and the Studio offers no Sales Invoice fallback for them. Discovering
    only the default family leaves every row of a self-billed mapping unresolvable. */
export async function discoverEveryFamily(
  entityId: string,
  families: readonly string[],
): Promise<DiscoveryResult[]> {
  const wanted = families.length ? families : ["sales"];
  const results: DiscoveryResult[] = [];
  for (const family of wanted) results.push(await discoverFields(entityId, family));
  return results;
}

/** The go-live checklist; the server, never the client, decides readiness. */
export function useReadiness(entityId: string | undefined) {
  return useQuery({
    queryKey: ["entities", entityId, "readiness"],
    queryFn: () => apiGet<ReadinessReport>(`/api/entities/${entityId}/readiness`),
    enabled: Boolean(entityId),
    // Coming back to the step after fixing a blocker must not show the stale list.
    refetchOnMount: "always",
  });
}

/** Maps, renders and validates one sample of one family; nothing is stored and the ASP is not called. */
export function runTestDocument(
  entityId: string,
  family: Family,
  sample?: unknown,
): Promise<TestDocumentResult> {
  return apiSend<TestDocumentResult>(
    "POST",
    `/api/entities/${entityId}/test-document`,
    sample === undefined ? { family } : { family, sample },
  );
}

export function goLive(entityId: string): Promise<Entity> {
  return apiSend<Entity>("POST", `/api/entities/${entityId}/go-live`);
}

/** The checklist a 409 NOT_READY carries back, or null when the body has none. */
export function notReadyItems(error: unknown): ReadinessItem[] | null {
  const body = (error as { body?: { items?: ReadinessItem[] } } | null)?.body;
  return Array.isArray(body?.items) ? body.items : null;
}
